import { IdentityApi, ConfigApi } from '@backstage/core-plugin-api';
import {
  RepositoryTypeItem,
  AlmServicesPluginApi,
  CreateDatabaseRequest,
  RequestResponse,
  ServiceAccountCreationRequest,
  PermissionsStructureRequest,
} from './types';

import axios, { AxiosInstance } from 'axios';

export class AlmServiceApiClient implements AlmServicesPluginApi {
  private readonly identityApi: IdentityApi;
  private readonly configApi: ConfigApi;
  private readonly axiosInstance: AxiosInstance;

  constructor(options: { identityApi: IdentityApi; configApi: ConfigApi }) {
    this.identityApi = options.identityApi;
    this.configApi = options.configApi;
    this.axiosInstance = axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.setupAuthInterceptor();
  }

  // Adds bearer token to each request automatically
  private setupAuthInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      async (request) => {
        const { token } = await this.identityApi.getCredentials();
        request.headers.Authorization = `Bearer ${token}`;
        return request;
      },
      (error: any) => Promise.reject(error),
    );
  }
  async createServiceAccount(
    requestPayload: ServiceAccountCreationRequest,
  ): Promise<{ error: any }> {
    const displayName = (await this.identityApi.getProfileInfo()).displayName;
    const url = `${this.getBaseUrl()}/api/alm/service-accounts`;

    const payload = JSON.stringify({
      serviceAccounts: requestPayload.serviceAccounts,
      requester: displayName,
    });
    try {
      await this.axiosInstance.post(url, payload);

      return { error: null };
    } catch (err) {
      return { error: err };
    }
  }

  async cancelRequest(id: string): Promise<{ error: any }> {
    const url = `${this.getBaseUrl()}/api/alm/cancel-request`;

    const response = await this.axiosInstance.patch(url, {
      id,
    });

    if (response.status !== 200) {
      const message = response.data as string;
      console.error(response.statusText, message);
      return { error: message };
    }

    return { error: null };
  }

  async repositoryTypes(): Promise<{
    data: RepositoryTypeItem[] | null;
    error: any;
  }> {
    const url = `${this.getBaseUrl()}/api/alm/repository-types`;
    try {
      const response = await this.axiosInstance.get(url);
      const json = response.data as RepositoryTypeItem[];
      return { data: json, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  async createDatabases(requestPayload: CreateDatabaseRequest): Promise<{
    error: any;
  }> {
    const displayName = (await this.identityApi.getProfileInfo()).displayName;
    const url = `${this.getBaseUrl()}/api/alm/databases`;

    const payload = JSON.stringify({
      databases: requestPayload.databases,
      requester: displayName,
    });
    try {
      await this.axiosInstance.post(url, payload);

      return { error: null };
    } catch (err) {
      return { error: err };
    }
  }

  async addServiceAccountPermissions(
    requestPayload: PermissionsStructureRequest,
  ): Promise<{
    error: any;
  }> {
    const displayName = (await this.identityApi.getProfileInfo()).displayName;
    const url = `${this.getBaseUrl()}/api/alm/add-service-account-permissions?requester=${encodeURIComponent(String(displayName))}`;

    const payload = JSON.stringify(requestPayload);
    try {
      await this.axiosInstance.put(url, payload);

      return { error: null };
    } catch (err) {
      return { error: err };
    }
  }

  async getRequests(
    skip = 0,
    take = 100,
  ): Promise<{
    data: RequestResponse | null;
    error: any;
  }> {
    const requestedBy = (await this.identityApi.getProfileInfo()).displayName;
    const url = `${this.getBaseUrl()}/api/alm/requests?requestedBy=${requestedBy}&skip=${skip}&take=${take}`;

    const response = await this.axiosInstance.get(url);

    if (response.status !== 200) {
      const message = response.data as string;
      console.error(response.statusText, message);
      return { data: null, error: message };
    }

    return { data: response.data as RequestResponse | null, error: null };
  }

  private getBaseUrl(): string {
    return this.configApi.getString('backend.baseUrl');
  }
}
